import { createContext, useContext, useEffect, useState } from 'react'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { getSidebarConfig } from '@/utils/responsive'

const ResponsiveContext = createContext()

/**
 * Responsive Context Provider
 * Provides global responsive state and utilities to all components
 */
export const ResponsiveProvider = ({ children }) => {
  const breakpointData = useBreakpoint()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [sidebarConfig, setSidebarConfig] = useState({})

  // Update sidebar configuration based on screen size
  useEffect(() => {
    const config = getSidebarConfig(breakpointData.screenSize.width)
    setSidebarConfig(config)
  }, [breakpointData.screenSize.width])

  // Auto-close sidebar when transitioning into mobile layout
  useEffect(() => {
    if (breakpointData.isMobile) {
      setSidebarOpen(false)
    }
  }, [breakpointData.isMobile])

  // Close sidebar when clicking outside on mobile
  const closeSidebar = () => {
    if (breakpointData.isMobile) {
      setSidebarOpen(false)
    }
  }

  // Toggle sidebar
  const toggleSidebar = () => {
    setSidebarOpen(prev => !prev)
  }

  const value = {
    // Breakpoint data
    ...breakpointData,
    
    // Sidebar state
    sidebarOpen,
    setSidebarOpen,
    sidebarConfig,
    closeSidebar,
    toggleSidebar,
    
    // Responsive helpers
    shouldShowMobileMenu: breakpointData.isMobile,
    shouldCollapseSidebar: breakpointData.isMobile || breakpointData.isTablet,
    shouldShowFullTable: breakpointData.isDesktop,
    
    // Layout helpers
    getContainerPadding: () => {
      if (breakpointData.isMobile) return 'px-4'
      if (breakpointData.isTablet) return 'px-6'
      return 'px-8'
    },
    
    getCardSpacing: () => {
      if (breakpointData.isMobile) return 'p-4 space-y-4'
      if (breakpointData.isTablet) return 'p-6 space-y-6'
      return 'p-8 space-y-8'
    },
    
    getButtonSize: () => {
      if (breakpointData.isMobile) return 'h-12 px-6 text-base'
      return 'h-10 px-4 text-sm'
    },
    
    getInputSize: () => {
      if (breakpointData.isMobile) return 'h-12 px-4 text-base'
      return 'h-10 px-3 text-sm'
    }
  }

  return (
    <ResponsiveContext.Provider value={value}>
      {children}
    </ResponsiveContext.Provider>
  )
}

/**
 * Hook to use responsive context
 * @returns {Object} Responsive context value
 */
export const useResponsive = () => {
  const context = useContext(ResponsiveContext)
  
  if (!context) {
    throw new Error('useResponsive must be used within a ResponsiveProvider')
  }
  
  return context
}

export default ResponsiveContext